import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardCheck } from 'lucide-react'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import type { EssentialCheckRatingItem, LcCoachingGuide } from '@/types/coaching'
import { useRateEssentialChecks } from '@/hooks/coaching/useRateEssentialCheck'
import { EssentialCheckRatingInput } from './EssentialCheckRatingInput'

interface Props {
  learnerId: number
  lessonId: number
  learningAttemptId: number | null
  guide?: LcCoachingGuide
  isLoading: boolean
  canEdit: boolean
}

export function UnderstandingCheckPanel({
  learnerId,
  lessonId,
  learningAttemptId,
  guide,
  isLoading,
  canEdit,
}: Props) {
  const { t } = useTranslation('coaching')
  const rate = useRateEssentialChecks(learnerId)
  const [draftRatings, setDraftRatings] = useState<Record<number, number>>({})
  const persistedRatingSignature = guide?.essentialChecks
    .map((check) => `${check.checkNo}:${check.score ?? ''}`)
    .join('|') ?? ''

  useEffect(() => {
    setDraftRatings({})
  }, [lessonId, learningAttemptId, persistedRatingSignature])

  const pendingRatings: EssentialCheckRatingItem[] = Object.entries(draftRatings).map(
    ([checkNo, score]) => ({ checkNo: Number(checkNo), score }),
  )

  function selectRating(checkNo: number, persistedScore: number | null, score: number) {
    setDraftRatings((current) => {
      if (score === persistedScore) {
        const next = { ...current }
        delete next[checkNo]
        return next
      }
      return { ...current, [checkNo]: score }
    })
  }

  async function saveRatings() {
    if (pendingRatings.length === 0 || rate.isPending) return
    try {
      await rate.mutateAsync({ lessonId, learningAttemptId, ratings: pendingRatings })
      setDraftRatings({})
    } catch {
      // Mutation layer owns the localized error toast. Keep drafts so the teacher can retry.
    }
  }

  if (isLoading || !guide) {
    return <p className="rounded-lg border bg-white p-4 text-sm text-gray-500" role="status">{t('coachingGuide.loading')}</p>
  }

  return (
    <div className="space-y-4 rounded-xl border border-lc-mint-200 bg-[linear-gradient(145deg,var(--color-lc-mint-100),white_42%,var(--color-lc-lightblue-100))] p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-xl bg-lc-mint-900 text-white shadow-[0_3px_0_0_var(--color-lc-mint-dark)]">
            <ClipboardCheck size={18} aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-sm font-bold text-gray-950">{t('operationStep.understanding')}</h3>
            <p className="text-xs text-gray-600">{t('coachingGuide.essentialCheck.batchHint')}</p>
          </div>
        </div>
        {!canEdit && (
          <span className="rounded bg-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-600">
            {t('operationStep.readOnly')}
          </span>
        )}
      </div>

      <section className="rounded-xl border border-lc-mint-200 bg-white/95 p-3.5 shadow-sm">
        <div className="mb-3 flex justify-end">
          <span className="rounded-full bg-lc-mint-100 px-2.5 py-1 text-[11px] font-bold text-lc-mint-dark">
            {t('coachingGuide.essentialCheck.itemCount', { count: guide.essentialChecks.length })}
          </span>
        </div>
        {guide.essentialChecks.length > 0 ? (
          <ul className="space-y-2.5">
            {guide.essentialChecks.map((check) => (
              <li
                key={check.checkNo}
                data-testid={`essential-check-${check.checkNo}`}
                className="rounded-xl border border-gray-200 border-l-lc-mint-500 border-l-4 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
              >
                <div className="flex items-start gap-2.5">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-lc-mint-100 text-xs font-bold text-lc-mint-dark">
                    {check.checkNo}
                  </span>
                  <p className="pt-0.5 text-sm font-medium leading-relaxed text-gray-800">
                    <LearningContentText
                      text={
                        check.text ??
                        check.label ??
                        t('coachingGuide.essentialCheck.untitled', { no: check.checkNo })
                      }
                    />
                  </p>
                </div>
                <div className="mt-3">
                  <div className="mb-1.5 flex items-center justify-between text-[11px] text-gray-500">
                    <span>{t('coachingGuide.essentialCheck.ratingLabel')}</span>
                    <span>{t('coachingGuide.essentialCheck.ratingRange')}</span>
                  </div>
                  <EssentialCheckRatingInput
                    value={draftRatings[check.checkNo] ?? check.score}
                    disabled={!canEdit || rate.isPending}
                    onChange={(score) => selectRating(check.checkNo, check.score, score)}
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs italic text-gray-500">{t('coachingGuide.essentialCheck.empty')}</p>
        )}

        {canEdit && guide.essentialChecks.length > 0 && (
          <div className="sticky bottom-[calc(0.5rem+var(--admin-mobile-grading-clearance,0px))] z-10 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white/95 px-3 py-2.5 shadow-md backdrop-blur">
            <div>
              <p className="text-sm font-bold text-gray-800">
                {pendingRatings.length > 0
                  ? t('coachingGuide.essentialCheck.pendingCount', { count: pendingRatings.length })
                  : t('coachingGuide.essentialCheck.noChanges')}
              </p>
              <p className="text-xs text-gray-500">{t('coachingGuide.essentialCheck.saveHint')}</p>
            </div>
            <button
              type="button"
              onClick={saveRatings}
              disabled={pendingRatings.length === 0 || rate.isPending}
              className="inline-flex min-w-28 items-center justify-center rounded-md bg-lc-mint-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-lc-mint-dark disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {rate.isPending
                ? t('coachingGuide.essentialCheck.saving')
                : t('coachingGuide.essentialCheck.save')}
            </button>
          </div>
        )}
      </section>

    </div>
  )
}
